"use client";

import { useActionAccess } from "@/components/guards/action-guard";
import { RelationshipDialog } from "@/components/relationship-dialog";
import { useCallback, useState } from "react";

/**
 * Custom hook to wrap actions with relationship guards
 *
 * This hook encapsulates the repetitive logic for checking user access and showing
 * the RelationshipDialog modal, reducing code duplication across components.
 *
 * @param actionId - The ID of the action from actions-relationship-mapping.json
 * @param actionFn - The function to execute if access is granted
 * @returns Object containing the guarded action function and the guard component
 *
 * @example
 * ```tsx
 * const { guardedAction, GuardComponent } = useGuardedAction(
 *   "shortlist_property",
 *   async () => {
 *     await handleShortlist(property, "search");
 *   }
 * );
 *
 * return (
 *   <>
 *     <Button onClick={guardedAction}>Shortlist</Button>
 *     <GuardComponent />
 *   </>
 * );
 * ```
 */
export function useGuardedAction<T extends unknown[]>(
	actionId: string,
	actionFn: (...args: T) => Promise<void> | void,
) {
	const { hasAccess, requiredRelationship, currentRelationship } = useActionAccess(actionId);
	const [showGuard, setShowGuard] = useState(false);

	const guardedAction = useCallback(
		async (...args: T) => {
			// Check relationship access before proceeding
			if (!hasAccess) {
				setShowGuard(true);
				return;
			}

			// Execute the actual action
			await actionFn(...args);
		},
		[hasAccess, actionFn],
	);

	const GuardComponent = useCallback(
		() =>
			showGuard &&
			currentRelationship &&
			(currentRelationship === "buyer" || currentRelationship === "seller") &&
			requiredRelationship &&
			(requiredRelationship === "buyer" || requiredRelationship === "seller") ? (
				<RelationshipDialog
					isOpen={showGuard}
					onClose={() => setShowGuard(false)}
					currentRelationship={currentRelationship}
					requiredRelationship={requiredRelationship}
					featureName="Shortlist/Heart Button"
					description="Save properties to favorites"
				/>
			) : null,
		[showGuard, currentRelationship, requiredRelationship],
	);

	return {
		guardedAction,
		GuardComponent,
		hasAccess,
	};
}
